import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import type { Vendor } from '@vendorhub/shared/domain/catalog';
import { VhBadgeComponent } from '../badge/badge.component';
import { VhCardComponent } from '../card/card.component';
import { VhIconComponent } from '../icon/icon.component';
import { VhImageComponent } from '../image/image.component';
import { VhRatingComponent } from '../rating/rating.component';

/**
 * Vendor / storefront tile. Hero image + name + location + rating.
 * Routes to the vendor detail page via RouterLink.
 */
@Component({
  selector: 'vh-vendor-card',
  standalone: true,
  imports: [
    RouterLink,
    VhCardComponent,
    VhImageComponent,
    VhRatingComponent,
    VhBadgeComponent,
    VhIconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <vh-card variant="elevated" [interactive]="true">
      <a vh-card-media [routerLink]="detailRoute()" class="vh-vendor-card__hero">
        <vh-image [src]="vendor().heroUrl" [alt]="vendor().name" aspectRatio="16/9" />
      </a>
      <div vh-card-header class="vh-vendor-card__header">
        <a [routerLink]="detailRoute()" class="vh-vendor-card__name">{{ vendor().name }}</a>
        @if (vendor().verified) {
          <vh-badge tone="brand" size="sm" icon="check">Verified</vh-badge>
        }
      </div>
      <div class="vh-vendor-card__body">
        @if (vendor().tagline) {
          <p class="vh-vendor-card__tagline">{{ vendor().tagline }}</p>
        }
        <div class="vh-vendor-card__location">
          <vh-icon name="map-pin" size="sm" />
          <span>{{ vendor().location.city }}, {{ vendor().location.state }}</span>
        </div>
      </div>
      <div vh-card-footer class="vh-vendor-card__footer">
        <vh-rating [value]="vendor().rating" [count]="vendor().reviewCount" size="sm" />
        <span class="vh-vendor-card__count">{{ vendor().productCount }} products</span>
      </div>
    </vh-card>
  `,
  styleUrl: './vendor-card.component.scss',
})
export class VhVendorCardComponent {
  readonly vendor = input.required<Vendor>();

  readonly detailRoute = computed(() => ['/vendors', this.vendor().slug]);
}
